// Phase 9 Track A — canonical axios-error-to-display-string helper.
//
// The operator console hit React error #31
// ("Objects are not valid as a React child") whenever a backend route
// returned a Pydantic v2 422 and the caller passed
// ``err.response.data.detail`` straight into a toast or a React state
// that later rendered as a JSX child. Pydantic v2 ships an array of
// error items with shape ``{type, loc, msg, input, url}`` — that array
// is truthy, so the common
//
//     toast.error(e?.response?.data?.detail || e?.message || "…")
//
// pattern passed it through unchanged.
//
// ``formatApiError`` is the one place where any axios-style error,
// a bare response body, or an already-formatted string gets coerced to
// a single readable display string. Every error-rendering site in the
// app should call this (or accept a string that came through it), so
// that React never sees a raw error object.
//
// It is intentionally narrow: string in → string out, nothing else.
// It does not toast, log, redirect, or throw. Keep it that way.

const MAX_OBJECT_LEN = 200;

// Pick the last non-empty segment of a Pydantic ``loc`` tuple —
// that's the actual field that failed. Everything before it is route /
// body-wrapper framing ("body", "query", "0"), which is noise to the
// operator.
function _locLabel(loc) {
    if (!Array.isArray(loc) || loc.length === 0) return "";
    for (let i = loc.length - 1; i >= 0; i -= 1) {
        const seg = loc[i];
        if (typeof seg === "string" && seg && seg !== "body" && seg !== "query") {
            return seg;
        }
        if (typeof seg === "number") return String(seg);
    }
    return "";
}

function _fmtPydanticItem(item) {
    if (!item || typeof item !== "object") return String(item || "");
    const field = _locLabel(item.loc);
    const msg = typeof item.msg === "string" && item.msg ? item.msg : "validation error";
    return field ? `${field}: ${msg}` : msg;
}

/**
 * Return a human-readable, single-line string for the given error.
 * Never returns null/undefined. Never returns a non-string.
 *
 * @param {*} err      An axios error, a bare response body, a string,
 *                     or anything at all.
 * @param {string} [fallback] Display when nothing more informative can
 *                     be extracted. Defaults to the generic
 *                     "Something went wrong." (the operator will at
 *                     least know it wasn't a UI crash).
 */
export function formatApiError(err, fallback = "Something went wrong.") {
    if (err == null) return fallback;

    // 1. String in → string out.
    if (typeof err === "string") return err || fallback;

    // 2. Axios shape: err.response.data.detail preferred; then the
    //    response body; then the transport-level err.message.
    const resp = err && typeof err === "object" ? err.response : null;
    const body = resp && typeof resp === "object" ? resp.data : undefined;

    // Network error heuristic — axios raises with no ``response``
    // object when the request itself never reached the server.
    if (err && typeof err === "object" && !resp
        && (err.code === "ERR_NETWORK"
            || err.message === "Network Error"
            || /network/i.test(err.message || ""))) {
        return "Network error — please retry";
    }

    // 3. If the body is a string (plain 500 text payload), use that.
    if (typeof body === "string" && body.trim()) {
        return body.length > MAX_OBJECT_LEN
            ? `${body.slice(0, MAX_OBJECT_LEN)}…`
            : body;
    }

    // 4. FastAPI's canonical ``{detail: …}`` shape.
    if (body && typeof body === "object") {
        const d = body.detail;

        // 4a. Plain string detail (our 409 + 403 + many 400s).
        if (typeof d === "string" && d) return d;

        // 4b. Pydantic v2 422: array of {type, loc, msg, input, url}.
        //     Join the first three items for readability.
        if (Array.isArray(d) && d.length > 0) {
            return d.slice(0, 3).map(_fmtPydanticItem).filter(Boolean).join("; ")
                || fallback;
        }
        // 4b'. Empty-array detail: nothing to say. Fall back.
        if (Array.isArray(d)) return fallback;

        // 4c. Nested {detail: {message: "…"}} used by a couple of
        //     services to carry structured failure info.
        if (d && typeof d === "object") {
            if (typeof d.message === "string" && d.message) return d.message;
            const serialised = _safeJson(d);
            return `Unexpected error: ${serialised}`;
        }

        // 4d. Some endpoints return {error: "…"} or {message: "…"}.
        if (typeof body.error === "string" && body.error) return body.error;
        if (typeof body.message === "string" && body.message) return body.message;
    }

    // 5. Transport-level message as the last resort before fallback.
    if (err && typeof err === "object" && typeof err.message === "string" && err.message) {
        return err.message;
    }

    // 6. Don't stringify huge objects into toasts. Truncate hard.
    if (err && typeof err === "object") {
        return `Unexpected error: ${_safeJson(err)}`;
    }

    return fallback;
}

function _safeJson(v) {
    try {
        const s = JSON.stringify(v);
        if (!s) return "";
        return s.length > MAX_OBJECT_LEN ? `${s.slice(0, MAX_OBJECT_LEN)}…` : s;
    } catch {
        return "[unserialisable]";
    }
}

export default formatApiError;
